import { getAudioContext } from "../audio/AudioController";

import * as ToneJS from "tone";
import { AudioContextStore } from "../stores/AudioContextStore";
import { MixerStore } from "../stores/MixerStore";
import { RydMixer } from "../audio/mixer";
import {
    CaretController,
    ScrollController
} from "../utils/ScrollController";
import {
    ProjectInstrumentAssignmentMap,
    ProjectTracksMap,
    ProjectUIState,
    RydProject
} from "../model/project";
import { NoteDuration } from "../model/core";
import { RollRedrawObserver } from "../utils/RollRedrawObserver";
import { ComputedContextFrame, ContextPlane } from "../model/context";
import { AbstractHorizontalContainer } from "../model/containers";
import { RydBookmark } from "../model/bookmark";
import { StateClassWrapper } from "../stores/WorkspaceStore";

export namespace RydInit {
    export function initAudioContext(audioContextStore: AudioContextStore) {
        if (audioContextStore.audioContext) return;

        console.log('INIT: AUDIO CONTEXT 2');
        console.time('init:audio-context');
        const audioContext = getAudioContext();

        audioContextStore.setAudioContext(audioContext);
        console.timeEnd('init:audio-context');
        console.time('init:tonejs');
        ToneJS.setContext(audioContext);
        ToneJS.start();
        console.timeEnd('init:tonejs');
    }

    export function initMixer(
        audioContextStore: AudioContextStore,
        mixerStore: MixerStore
    ) {
        // console.log('INIT: MIXER 1');
        if (!audioContextStore.audioContext) return;
        // console.log('INIT: MIXER 2');
        if (!mixerStore.mixer) {
            console.log('INIT: MIXER 3');
            mixerStore.setMixer(new RydMixer.Mixer(audioContextStore.audioContext));
        }
    }

    export function initScrollAndCarret(
        project: RydProject,
        zoomFactor: number,
        setCaretController: (cc: CaretController) => void,
        setRedrawObserver: (rro: RollRedrawObserver) => void,

    ) {
        console.log('INIT: SCROLL/CARET');
        console.time('init:scroll/caret');

        const scrollController = ScrollController.getInstance();
        scrollController.clearListeners();

        const tracks = project.getAll();

        const cc = new CaretController({
            scrollController,
            resolution: new NoteDuration(1),
            // FIXME: we just take the greatest PPQ of all tracks. This probably causes issues when
            // there are different PPQs.
            ppq: project.getAll().map(l => l.getPPQ()).reduce((a, b) => Math.max(a, b), 1),
            zoomFactor: zoomFactor
        });
        setCaretController(cc);
        setRedrawObserver(new RollRedrawObserver());

        console.timeEnd('init:scroll/caret');
    }

    /**
     *
     * @param project
     * @param audioContextStore
     * @param mixerStore
     * @param setWorkspaceTracks
     * @param setWorkspaceContextFrame
     * @param setWorkspaceLines
     * @param setWorkspaceUIState
     * @param setWorkspaceInstrumentAssignments
     * @param initialized
     * @param setInitialized
     * @returns A function to cleanup the current project (removing tracks from mixer, etc.).
     *          Usually for use as return of a React's useEffect.
     */
    export function initWorkspaceState(
        project: RydProject,
        audioContextStore: AudioContextStore,
        mixerStore: MixerStore,
        setWorkspaceTracks: (tracks: ProjectTracksMap) => void,
        setWorkspaceContextPlane: (cp: StateClassWrapper<ContextPlane>) => void,
        setWorkspaceContextFrame: (frame: ComputedContextFrame) => void,
        setWorkspaceLines: (lines: AbstractHorizontalContainer<any>[]) => void,
        setWorkspaceUIState: (uiState: ProjectUIState) => void,
        setWorkspaceInstrumentAssignments: (ia: ProjectInstrumentAssignmentMap) => void,
        setWorkspaceBookmarks: (b: RydBookmark[]) => void,
        initialized: boolean,
        setInitialized: (v: boolean) => void,
    ) {
        console.log('INIT STATE enter', project, audioContextStore.audioContext, mixerStore.mixer);
        if (!project) return;
        if (!audioContextStore.audioContext) return;
        if (!mixerStore.mixer) return;

        console.log('INIT: STATE', project);
        console.time('init:state');

        setWorkspaceTracks(new Map(project.tracks));
        setWorkspaceContextPlane({ stateObject: project.contextFrame.grid });
        setWorkspaceContextFrame(project.contextFrame.computed);
        setWorkspaceLines(project.getAll());
        setWorkspaceUIState(project.uiState);
        setWorkspaceInstrumentAssignments(project.instrumentAssignments);
        setWorkspaceBookmarks(project.bookmarks);
        mixerStore.forceUpdate();

        console.timeEnd('init:state');

        console.log('INIT: MIXER-TRACKS', project);

        console.time('init:mixer-tracks');
        RydInit.initMixer(audioContextStore, mixerStore);

        console.log('INIT: MIXER-TRACKS 2 src', project.mixerState);
        mixerStore.mixer?.removeAllTracks();
        project.mixerState.tracks.forEach((track, i) => {
            if (!mixerStore.mixer) {
                console.warn('No mixer in store, cannot add tracks');
                return;
            }
            mixerStore.mixer?.addTrack({
                id: track.id,
                name: track.name,
                gain: track.gain,
                pan: track.pan,
                muted: track.muted,
                solo: track.solo,
                sourceNode: project.instrumentAssignments.get(i)?.getOutputNode(),
                onBus: track.onBus
            });
        });
        mixerStore.mixer?.masterGain.gain.setValueAtTime(project.mixerState.master.gain, 0);
        mixerStore.forceUpdate();
        setInitialized(true);

        console.timeEnd('init:mixer-tracks');

        return () => {
            console.log('CLEANUP: CURRENT PROJECT');
            mixerStore.mixer?.removeAllTracks();
            setInitialized(false);
        };
    }
}